import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from 'react-query'
import { SitesApi as LegacySitesApi } from '@trybeapp/sdk.shop'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { apiConfiguration, formatDateForApi } from 'utilities/TrybeApiClient'
import { sdkDateToSystemDateTime } from 'utilities/DateUtils/dateUtils'
import {
  CreateShopSiteOpeningHoursOverride,
  CreateShopSiteOpeningHoursOverrideResponse,
  DeleteShopSiteOpeningHoursOverrideRequest,
  GetShopSite,
  ResponseError,
  ShopSite,
  SitesApi,
  UpdateShopSite,
  UpdateShopSiteResponse,
} from '@trybeapp/sdk'

const legacySitesApi = new LegacySitesApi()
const sitesApi = new SitesApi(apiConfiguration)

export const useShopSite = (
  siteId?: ShopSite['id'] | null,
  options?: UseQueryOptions<GetShopSite>
) => {
  const currentSiteId = useCurrentSiteId()
  const id = siteId ?? currentSiteId

  return useQuery(['shopSites', id], () => sitesApi.getShopSite({ siteId: id }), {
    cacheTime: Infinity,
    staleTime: Infinity,
    ...options,
  })
}

export const useUpdateShopSite = (siteId: ShopSite['id']) => {
  const queryClient = useQueryClient()

  return useMutation<UpdateShopSiteResponse, ResponseError, UpdateShopSite>(
    (values) => sitesApi.updateShopSite({ siteId, updateShopSite: values }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shopSites', siteId])
        queryClient.invalidateQueries(['sites', siteId])
      },
    }
  )
}

export const usePreviewBrandEmailConfig: any = (
  siteId: string,
  config,
  options?: UseQueryOptions
): UseQueryResult =>
  useQuery(
    ['brandEmailConfigPreview', { siteId, ...config }],
    () => legacySitesApi.previewEmailBranding(siteId, config),
    options
  )

export const useOpeningHourOverrides = () => {
  const siteId = useCurrentSiteId()

  return useQuery(['openingHourOverrides', { siteId }], async () => {
    const response = await sitesApi.listShopSiteOpeningHoursOverrides({ siteId })

    response.data.forEach((override) => {
      if (override.date_from) {
        override.date_from = sdkDateToSystemDateTime(override.date_from).toJSDate()
      }

      if (override.date_to) {
        override.date_to = sdkDateToSystemDateTime(override.date_to).toJSDate()
      }
    })

    return response
  })
}

export const useCreateOpeningHourOverride = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<
    CreateShopSiteOpeningHoursOverrideResponse,
    ResponseError,
    CreateShopSiteOpeningHoursOverride
  >(
    (createShopSiteOpeningHoursOverride: CreateShopSiteOpeningHoursOverride) => {
      const formattedReqBody = {
        ...createShopSiteOpeningHoursOverride,
        date_from: formatDateForApi(createShopSiteOpeningHoursOverride.date_from),
        date_to: formatDateForApi(createShopSiteOpeningHoursOverride.date_to),
      }

      return sitesApi.createShopSiteOpeningHoursOverride({
        siteId,
        createShopSiteOpeningHoursOverride: formattedReqBody,
      })
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['openingHourOverrides', { siteId }]),
    }
  )
}

export const useDeleteOpeningHourOverride = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<
    void,
    ResponseError,
    DeleteShopSiteOpeningHoursOverrideRequest['openingHoursOverrideId']
  >(
    (overrideId) =>
      sitesApi.deleteShopSiteOpeningHoursOverride({
        siteId,
        openingHoursOverrideId: overrideId,
      }),
    {
      onSuccess: () => queryClient.invalidateQueries(['openingHourOverrides', { siteId }]),
    }
  )
}
